import { useDataLayer } from '@/hooks/useDataLayer';
import { useInViewChange } from '@/hooks/useInViewChange';
import type { Ga4Item, ListIdentity } from '@/types';
import { type RefObject, useCallback } from 'react';

export const useTrackItemList = (
  ref: RefObject<Element | null>,
  list: ListIdentity,
  items: Ga4Item[],
  signature?: string,
) => {
  const { viewItemList, selectItem } = useDataLayer();

  useInViewChange(ref, () => viewItemList(list, items), {
    // A product grid can easily be taller than the viewport (the chart page
    // alone can run to thousands of px), so the fractional default threshold
    // could never be satisfied and the list would never report as viewed.
    // 0 is also the semantically correct rule for an impression: the user
    // saw the list, not "saw half of it".
    threshold: 0,
    disabled: items.length === 0,
    signature,
  });

  // Depend on the primitives, not the object: callers build `list` inline, so
  // a fresh object every render would churn this callback continuously.
  const onSelectItem = useCallback(
    (item: Ga4Item, index?: number) =>
      selectItem({ name: list.name, id: list.id }, item, index),
    [selectItem, list.name, list.id],
  );

  return { onSelectItem };
};
